import React from 'react';
import {
  Input as BaseInput,
  InputOwnProps as BaseInputProps,
} from '@mui/base/Input';
import { Box } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import Typography from '@ui/Typography';
import useKeyboardFocus from '../../lib/useKeyboardFocus';

export type InputProps = Omit<BaseInputProps, 'error' | 'id' | 'value'> & {
  /**
   * Input type from base props is unknown, value could be any
   * For now assuming, this is primarily string input field
   */
  value?: string;
  classes?: {
    input?: string;
    div?: string;
    label?: string;
    container?: string;
  };
  label: string;
  fullWidth?: boolean;
  error?: string;
  id: string;
  maxLength?: number;
  focusColor?: string;
};

const useStyles = makeStyles<{ fullWidth?: boolean }>()(
  (theme, { fullWidth }) => {
    const inputTheme = theme.palette.ui.input;
    return {
      input: {
        padding: theme.spacing(1, 1.5),
        borderRadius: theme.spacing(0.5),
        border: 'none',
        outline: 'none',
        width: 'inherit',
        backgroundColor: inputTheme.primary.surface.default,
        color: inputTheme.primary.text.default,
        '&::placeholder': {
          color: inputTheme.primary.text.placeholder,
        },
        fontFamily: theme.typography.fontFamily,
      },
      div: {
        borderRadius: theme.spacing(0.5),
        outline: `1px solid ${inputTheme.primary.stroke.default}`,
        '&.base--error': {
          outline: `2px solid ${theme.palette.error.light}`,
        },
        '&.base--disabled': {
          cursor: 'not-allowed',
          '& > input': {
            cursor: 'not-allowed',
            opacity: 0.5,
          },
        },
        '&.base--focused': {
          outline: `1px solid ${inputTheme.primary.stroke.active}`,
        },
        width: fullWidth ? '-webkit-fill-available' : 'fit-content',
        '@supports ( -moz-appearance:none )': {
          width: fullWidth ? '-moz-available' : 'fit-content',
        },
      },
      labelContainer: {
        '&:hover': {
          cursor: 'pointer',
        },
      },
      label: {
        color: inputTheme.primary.text.label,
      },
      keyboardFocus: {
        outline: `2px solid ${inputTheme.primary.stroke.focus}`,
      },
      counter: {
        color: inputTheme.primary.text.default,
        justifySelf: 'end',
      },
      counterHidden: {
        visibility: 'hidden',
      },
      disabled: {
        opacity: 0.5,
      },
    };
  }
);

const Input = (props: InputProps) => {
  const {
    classes: slotClasses,
    label,
    fullWidth: incomingFullWidth,
    error,
    id,
    maxLength,
    focusColor,
    ...rest
  } = props;

  const { value } = props;
  const { classes, cx } = useStyles({
    fullWidth: maxLength ? true : incomingFullWidth,
  });
  // Without the casting, this is a pain to resolve.. ??
  const restTyped = { ...rest } as BaseInputProps;

  const [isFocused, setIsFocused] = React.useState(false);
  const inputRef = React.useRef<HTMLInputElement>(null);
  // note: this is a deprecated hook..
  useKeyboardFocus({
    ref: inputRef,
    // override the default focus color class if a custom focus color is provided
    // note: we cannot use the typical function props pattern here due to a thrown
    // exception from the useKeyboardFocus hook.
    className: focusColor ? undefined : classes.keyboardFocus,
  });

  const handleFocus = React.useCallback(() => {
    if (inputRef && inputRef.current) {
      inputRef.current.select();
    }
    setIsFocused(true);
  }, []);

  const handleBlur = React.useCallback(() => {
    setIsFocused(false);
  }, []);

  return (
    <Box display="grid" gap={1} className={slotClasses?.container}>
      <label
        htmlFor={id}
        className={cx(classes.labelContainer, slotClasses?.label, {
          [classes.disabled]: restTyped.disabled,
        })}
      >
        <Typography variant="pSm" bold className={classes.label}>
          {label}
        </Typography>
      </label>
      <BaseInput
        id={id}
        slotProps={{
          input: {
            className: cx(classes.input, slotClasses?.input),
            ref: inputRef,
            maxLength,
            onFocus: handleFocus,
            onBlur: handleBlur,
          },
          root: {
            className: cx(classes.div, slotClasses?.div),
          },
        }}
        error={Boolean(error?.trim().length)}
        {...restTyped}
      />
      {(error || maxLength) && (
        <Box display="grid" gridAutoFlow="column" alignItems="center">
          {error && (
            <Typography variant="pMd" color="error">
              {error}
            </Typography>
          )}
          {maxLength && (
            <Typography
              variant="pSm"
              className={cx(classes.counter, {
                [classes.counterHidden]: !(isFocused || error),
              })}
              aria-hidden={!(isFocused || error)}
            >{`${Math.max(value?.length || 0, 0)}/${maxLength}`}</Typography>
          )}
        </Box>
      )}
    </Box>
  );
};

export default Input;
